import {
  CircularProgress,
} from '@mui/material';
import { Role } from 'API';
import { ENV } from 'env-config';
import Dashboard from 'pages/Dashboard';
import NotFound from 'pages/NotFound';
import React, { Suspense, lazy, useContext } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { ALL_ROLES, MH_ROLES } from 'services/auth';
import UserContext from 'shared/contexts/UserContext';
import { ROUTES } from 'shared/routes';
import Layout from './Layout';

const Playground = lazy(() => import('pages/Playground'));
const UserAgreement = lazy(() => import('components/UserAgreement'));
const Contacts = lazy(() => import('pages/Contacts'));
const Case = lazy(() => import('pages/Case'));
const Cases = lazy(() => import('pages/Cases'));
const Notifications = lazy(() => import('pages/Notifications'));
const Resources = lazy(() => import('pages/Resources'));
const Admin = lazy(() => import('pages/Admin'));
const Tasks = lazy(() => import('pages/Tasks'));
const CreateReport = lazy(() => import('pages/CreateReport'));
const Report = lazy(() => import('pages/Report'));

type RouteConfig = {
  paths: string[];
  Component: React.ComponentType;
  roles: Role[];
};

const ROUTES_CONFIG: RouteConfig[] = [
  {
    paths: [ROUTES.dashboard],
    Component: Dashboard,
    roles: ALL_ROLES,
  },
  {
    paths: [
      `${ROUTES.contactsLanding}/*`,
      `${ROUTES.people}/*`,
      `${ROUTES.companies}/*`,
    ],
    Component: Contacts,
    roles: ALL_ROLES,
  },
  {
    paths: [
      `${ROUTES.cases}/:slug/:initialTab`,
      `${ROUTES.cases}/:slug`,
    ],
    Component: Case,
    roles: ALL_ROLES,
  },
  {
    paths: [
      `${ROUTES.processRequests}/*`,
      `${ROUTES.cases}/*`,
    ],
    Component: Cases,
    roles: [Role.ADMIN, Role.TEAMMEMBER, Role.EMPLOYER],
  },
  {
    paths: [ROUTES.notifications],
    Component: Notifications,
    roles: ALL_ROLES,
  },
  {
    paths: [ROUTES.resources],
    Component: Resources,
    roles: ALL_ROLES,
  },
  {
    paths: [`${ROUTES.admin}/*`],
    Component: Admin,
    roles: MH_ROLES,
  },
  {
    paths: [ROUTES.tasks],
    Component: Tasks,
    roles: ALL_ROLES,
  },
  {
    paths: [ROUTES.createReport, `${ROUTES.createReport}/:id`],
    Component: CreateReport,
    roles: [Role.ADMIN, Role.TEAMMEMBER, Role.EMPLOYER],
  },
  {
    paths: [`${ROUTES.reports}/*`],
    Component: Report,
    roles: [Role.ADMIN, Role.TEAMMEMBER, Role.EMPLOYER],
  },
];

const Routing: React.FC = () => {
  const [userState] = useContext(UserContext);
  const { person, role } = userState;

  if (!person.userAgreementAcknowledged) {
    return (
      <Suspense fallback={<CircularProgress />}>
        <UserAgreement />
      </Suspense>
    );
  }

  return (
    <Router>
      <Layout>
        <Routes>
          {ROUTES_CONFIG.flatMap(({ paths, Component, roles }) => {
            const element = roles.includes(role) ? (
              <Suspense fallback={<CircularProgress />}>
                <Component />
              </Suspense>
            ) : (
              <Navigate to={ROUTES.dashboard} replace />
            );

            return paths.map((path) => (
              <Route key={path} path={path} element={element} />
            ));
          })}
          {ENV !== 'prod' && (
            <Route
              path="/playground"
              element={(
                <main className="page-grid__main content">
                  <Suspense fallback={<CircularProgress />}>
                    <Playground />
                  </Suspense>
                </main>
              )}
            />
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </Router>
  );
};

export default Routing;
